// src/components/PropertyView/PropertyDetailsTabs.js
// Sistema de pestañas para organizar detalles de propiedad
// ✅ Separado de PropertyDetailsCard para facilitar cambios de layout (horizontal/vertical)

import React, { useState } from 'react';
import { Home, Building, Wrench, DollarSign, FileText } from 'lucide-react';
import {
  sectionsConfig,
  renderSection,
  renderCustomFields,
  hasSectionData
} from './PropertyDetailsCard';
import { useTranslation } from 'react-i18next';

/**
 * CONFIGURACIÓN DE PESTAÑAS
 * Agrupa secciones en pestañas lógicas (labels traducidos en el componente)
 */
const tabsConfig = [
  {
    id: 'property',
    labelKey: 'details.tabs.property',
    icon: Home,
    sections: ['physical']
  },
  {
    id: 'building',
    labelKey: 'details.tabs.building',
    icon: Building,
    sections: ['building', 'associatedSpaces']
  },
  {
    id: 'installations',
    labelKey: 'details.tabs.installations',
    icon: Wrench,
    sections: ['condition', 'installations', 'security']
  },
  {
    id: 'financial',
    labelKey: 'details.tabs.financial',
    icon: DollarSign,
    sections: ['costs', 'community', 'legal']
  },
  {
    id: 'custom',
    labelKey: 'details.tabs.custom',
    icon: FileText,
    sections: ['customFields'] // Sección especial
  }
];

/**
 * Componente principal de pestañas para detalles de propiedad
 * @param {object} property - Objeto de propiedad con propertyMetadata
 * @param {function} onOpenDocument - Callback para abrir documento
 */
export const PropertyDetailsTabs = ({ property, onOpenDocument, trackEvent, translations }) => {
  const { t } = useTranslation();
  const metadata = property.propertyMetadata || {};
  const documents = property.documentUrls || [];
  const [activeTab, setActiveTab] = useState(0);

  /**
   * Verificar si una pestaña tiene datos para mostrar
   */
  const hasTabData = (tab) => {
    // Caso especial: pestaña de documentos
    if (tab.id === 'documents') {
      return documents.length > 0;
    }

    // Caso especial: pestaña de customFields
    if (tab.sections.includes('customFields')) {
      return metadata.customFields && Object.keys(metadata.customFields).length > 0;
    }

    // Verificar si alguna de las secciones de la pestaña tiene datos
    return tab.sections.some(sectionKey => {
      const sectionConfig = sectionsConfig[sectionKey];
      return sectionConfig && hasSectionData(sectionConfig, metadata);
    });
  };

  /**
   * Filtrar pestañas que tienen datos
   */
  const visibleDetailTabs = tabsConfig.filter(hasTabData);

  /**
   * Tab de documentos (se añade al final con estilo especial)
   */
  const documentsTab = documents.length > 0 ? {
    id: 'documents',
    labelKey: 'details.tabs.documents',
    icon: FileText,
    isDocuments: true,
    count: documents.length
  } : null;

  /**
   * Todas las tabs visibles
   */
  const visibleTabs = documentsTab
    ? [...visibleDetailTabs, documentsTab]
    : visibleDetailTabs;

  // Si no hay datos en ninguna pestaña, mostrar mensaje
  if (visibleTabs.length === 0) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50">
        <h2 className="text-lg md:text-xl font-bold text-white mb-4">
          {t('details.title')}
        </h2>
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            {t('details.noData')}
          </p>
        </div>
      </div>
    );
  }

  /**
   * Renderizar contenido de una pestaña
   */
  const renderTabContent = (tab) => {
    // Caso especial: pestaña de documentos
    if (tab.id === 'documents') {
      return (
        <div className="flex flex-col gap-2">
          {documents.map((doc, index) => (
            <button
              key={index}
              onClick={() => onOpenDocument?.(doc)}
              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700/50 hover:border-amber-500/50 hover:bg-slate-800/50 transition-all group text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-amber-500/10 rounded-lg flex-shrink-0">
                  <FileText className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-sm text-slate-200 truncate">
                  {doc.name}
                </span>
              </div>
              <span className="text-xs text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2">
                {t('details.viewDoc')}
              </span>
            </button>
          ))}
        </div>
      );
    }

    // Caso especial: pestaña de customFields
    if (tab.sections?.includes('customFields')) {
      const customContent = renderCustomFields(metadata, translations);
      if (!customContent) return null;

      return (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-3">{t('details.customFieldsTitle')}</h3>
            {customContent}
          </div>
        </div>
      );
    }

    // Renderizar secciones normales
    return (
      <div className="space-y-4">
        {tab.sections.map(sectionKey => {
          const sectionConfig = sectionsConfig[sectionKey];
          if (!sectionConfig) return null;
          return renderSection(sectionKey, sectionConfig, metadata, t, translations);
        })}
      </div>
    );
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50">
      {/* Header */}
      <h2 className="text-lg md:text-xl font-bold text-white mb-4">
        {t('details.title')}
      </h2>

      {/* Pestañas horizontales */}
      <div className="flex border-b border-slate-700/50 mb-6 overflow-x-auto">
        {visibleTabs.map((tab, index) => {
          const IconComponent = tab.icon;
          const isActive = activeTab === index;
          const isDocuments = tab.isDocuments;
          const tabLabel = t(tab.labelKey);

          return (
            <React.Fragment key={tab.id}>
              {isDocuments && visibleDetailTabs.length > 0 && (
                <div className="flex-1 flex items-center justify-end pr-2">
                </div>
              )}
              <button
                onClick={() => {
                  setActiveTab(index);
                  if (trackEvent) {
                    trackEvent('tab_view', { tabName: tabLabel });
                  }
                }}
                className={`
                  flex items-center gap-2 px-4 py-3 font-medium transition-all
                  border-b-2 whitespace-nowrap
                  ${isDocuments
                    ? isActive
                      ? 'border-rose-500 text-rose-400 bg-rose-500/10'
                      : 'border-transparent text-rose-400/70 hover:text-rose-400 hover:bg-rose-500/5'
                    : isActive
                      ? 'border-amber-500 text-white bg-slate-700/30'
                      : 'border-transparent text-slate-400 hover:text-white hover:bg-slate-700/20'
                  }
                `}
              >
                {isDocuments && <IconComponent className="w-4 h-4" />}
                <span className="text-sm">{tabLabel}</span>
                {isDocuments && tab.count && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-rose-500/20 text-rose-400 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Contenido de la pestaña activa */}
      <div className="animate-fadeIn min-h-[240px]">
        {renderTabContent(visibleTabs[activeTab])}
      </div>
    </div>
  );
};

export default PropertyDetailsTabs;
